import { useEffect, useMemo, useState } from "react";
import { PageHeader, Stat, formatCZK, formatQty, formatDateTime } from "@/components/Shared";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Factory, Truck, History as HistoryIcon, Boxes, Trash2, Calendar, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatQtyWithPackage } from "@/lib/units";
import CostVarianceBadge from "@/components/CostVarianceBadge";
import { computeVariance } from "@/lib/costVariance";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function dateOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function startOfWeek() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // monday=0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function startOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [matAvail, setMatAvail] = useState([]);
  const [prodAvail, setProdAvail] = useState([]);
  const [branches, setBranches] = useState([]);
  const [wo, setWo] = useState(null);
  const [autoStats, setAutoStats] = useState(null);

  useEffect(() => {
    (async () => {
      const calls = [
        api.get("/production"),
        api.get("/products", { params: { include_deleted: true } }),
        api.get("/materials"),
        api.get("/stock/material-availability"),
        api.get("/stock/product-availability"),
        api.get("/branches"),
      ];
      const [r, p, m, mA, pA, b] = await Promise.all(calls);
      setRuns(r.data);
      setProducts(p.data);
      setMaterials(m.data);
      setMatAvail(mA.data);
      setProdAvail(pA.data);
      setBranches(b.data);
      // Auto-closed shifts (jen pro admin/manager)
      if (user?.role === "admin" || user?.role === "manager") {
        try {
          const ac = await api.get("/attendance/auto-closed-stats", { params: { days: 7 } });
          setAutoStats(ac.data);
        } catch {
          /* user without permission */
        }
      }
    })();
  }, [user]);

  useEffect(() => {
    (async () => {
      const params = { date_from: from, date_to: to };
      const s = await api.get("/dashboard/summary", { params });
      setSummary(s.data);
      if (user?.role === "admin") {
        try {
          const w = await api.get("/writeoffs/summary", { params });
          setWo(w.data);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [from, to, user]);

  const productName = (id) => products.find((p) => p.id === id)?.name || "—";
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  // Filtruj výroby pro vybrané období (from/to)
  const periodRuns = useMemo(() => {
    if (!from || !to) return [];
    const toEnd = to + "T23:59:59";
    return runs.filter((r) => r.created_at >= from && r.created_at <= toEnd);
  }, [runs, from, to]);

  // Sleduj jen výroby výrobků s nastaveným plánem
  const varianceStats = useMemo(() => {
    let compared = 0;
    let outOfTolerance = 0;
    const offenders = [];
    for (const r of periodRuns) {
      const product = products.find((p) => p.id === r.product_id);
      const v = computeVariance(r.unit_cost, product);
      if (!v) continue;
      compared += 1;
      if (v.overTolerance) {
        outOfTolerance += 1;
        offenders.push({ run: r, product, variance: v });
      }
    }
    // top 5 nejvíce mimo (podle abs pct)
    offenders.sort((a, b) => Math.abs(b.variance.pct) - Math.abs(a.variance.pct));
    return { compared, outOfTolerance, offenders: offenders.slice(0, 5) };
  }, [periodRuns, products]);

  // Posledních 5 výrob (globálně, pro tabulku "Poslední výroby")
  const recentRuns = useMemo(() => runs.slice(0, 5), [runs]);

  const presets = useMemo(
    () => [
      { key: "today", label: "Dnes", from: todayStr(), to: todayStr() },
      { key: "yesterday", label: "Včera", from: dateOffset(-1), to: dateOffset(-1) },
      { key: "week", label: "Tento týden", from: startOfWeek(), to: todayStr() },
      { key: "month", label: "Tento měsíc", from: startOfMonth(), to: todayStr() },
      { key: "30d", label: "Posledních 30 dní", from: dateOffset(-29), to: todayStr() },
    ],
    []
  );
  const activePreset = presets.find((p) => p.from === from && p.to === to)?.key;
  const periodLabel =
    from === to
      ? `pro ${new Date(from).toLocaleDateString("cs-CZ")}`
      : `${new Date(from).toLocaleDateString("cs-CZ")} – ${new Date(to).toLocaleDateString("cs-CZ")}`;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Přehled"
        title="Dobrý den v Caféčku"
        description="Vyberte období (dnes, týden, měsíc nebo vlastní rozsah). Všechny KPI a přehledy se filtrují podle zvoleného období."
      />

      <div className="card-cafe p-4 mb-8 flex flex-wrap items-center gap-3">
        <Calendar className="w-5 h-5 text-[#3A2D27]" />
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button
              key={p.key}
              data-testid={`dashboard-preset-${p.key}`}
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
              }}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                activePreset === p.key
                  ? "bg-[#3A2D27] text-white border-[#3A2D27]"
                  : "border-[#E5E1D8] hover:bg-[#F9F6F0] text-[#3A2D27]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-[#57534E]">Od</span>
          <input
            data-testid="dashboard-date-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input-cafe py-1.5"
          />
          <span className="text-xs text-[#57534E]">Do</span>
          <input
            data-testid="dashboard-date-to"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="input-cafe py-1.5"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-10">
        <Stat
          label="Vyrobeno (ks)"
          value={formatQty(summary?.today_production_qty)}
          hint={periodLabel}
        />
        <Stat
          label="Výrobní náklady"
          value={formatCZK(summary?.today_production_cost)}
          hint={periodLabel}
          accent="#3A2D27"
        />
        <Stat
          label="Expedováno (ks)"
          value={formatQty(summary?.today_dispatch_qty)}
          hint={periodLabel}
        />
        <Stat
          label="Odepsáno"
          value={formatCZK(summary?.today_writeoff_cost)}
          hint={`${formatQty(summary?.today_writeoff_qty)} ks · ${periodLabel}`}
          accent="#9E4747"
        />
        <Stat
          label="Mimo toleranci"
          value={
            varianceStats.compared > 0
              ? `${varianceStats.outOfTolerance} / ${varianceStats.compared}`
              : "—"
          }
          hint={
            varianceStats.compared === 0
              ? "Žádný porovnatelný výrobek v období"
              : varianceStats.outOfTolerance === 0
              ? "Vše v toleranci"
              : `${periodLabel}`
          }
          accent={varianceStats.outOfTolerance > 0 ? "#9E4747" : "#376A4D"}
        />
      </div>

      {varianceStats.offenders.length > 0 && (
        <div
          data-testid="dashboard-variance-alerts"
          className="card-cafe p-5 mb-8 border-[#9E47474D]"
          style={{ background: "#9E47470A" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#9E4747] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow mb-1 text-[#9E4747]">Pozor — výroba mimo plánovanou cenu</div>
              <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Outfit" }}>
                Top {varianceStats.offenders.length} odchylek {periodLabel}
              </h3>
              <ul className="space-y-1.5">
                {varianceStats.offenders.map(({ run, product, variance }) => (
                  <li
                    key={run.id}
                    className="flex items-center justify-between gap-3 text-sm flex-wrap"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="mono text-xs text-[#57534E]">{run.batch_code}</span>
                      <span className="font-medium truncate">{product?.name || run.product_id}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#57534E]">
                      <span>
                        plán {formatCZK(variance.planned)} → skut. {formatCZK(variance.actual)}
                      </span>
                      <CostVarianceBadge actualUnitCost={run.unit_cost} product={product} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {autoStats && autoStats.shifts_count > 0 && (
        <Link
          to="/dochazka-prehled"
          data-testid="dashboard-auto-closed-alert"
          className="block card-cafe p-5 mb-8 border-[#C29329] hover:border-[#8A6A1F] transition-colors"
          style={{ background: "#C293291A" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#8A6A1F] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow mb-1 text-[#8A6A1F]">Zapomenuté odchody (7 dní)</div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>
                {autoStats.user_count === 1
                  ? "1 zaměstnanec zapomněl odepsat odchod"
                  : `${autoStats.user_count} zaměstnanců zapomnělo odepsat odchod`}
              </h3>
              <div className="text-sm text-[#57534E] mb-2">
                Systém auto-uzavřel <strong>{autoStats.shifts_count}</strong>{" "}
                {autoStats.shifts_count === 1 ? "směnu" : autoStats.shifts_count < 5 ? "směny" : "směn"}{" "}
                15 hodin po nástupu. Klikněte pro kontrolu.
              </div>
              {autoStats.users.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {autoStats.users.slice(0, 8).map((u) => (
                    <li
                      key={u.user_id}
                      className="text-xs px-2 py-1 rounded-full bg-white border border-[#C2932966]"
                    >
                      <span className="font-medium">{u.name}</span>
                      {u.count > 1 && (
                        <span className="ml-1 text-[#8A6A1F]">×{u.count}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Link>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link
            to="/vyroba"
            className="card-cafe p-6 hover:border-[#D4A373] block transition-colors"
            data-testid="dashboard-go-production"
          >
            <Factory className="w-6 h-6 text-[#3A2D27] mb-3" />
            <div className="label-eyebrow mb-1">Akce</div>
            <div className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>
              Spustit výrobu
            </div>
            <p className="text-sm text-[#57534E] mt-1">Klikněte na výrobek a zadejte ks.</p>
          </Link>

          <Link
            to="/expedice"
            className="card-cafe p-6 hover:border-[#D4A373] block transition-colors"
            data-testid="dashboard-go-dispatch"
          >
            <Truck className="w-6 h-6 text-[#3A2D27] mb-3" />
            <div className="label-eyebrow mb-1">Akce</div>
            <div className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>
              Expedovat na pobočku
            </div>
            <p className="text-sm text-[#57534E] mt-1">Přesun výrobků z výrobního skladu.</p>
          </Link>

          <div className="card-cafe p-6">
            <Boxes className="w-6 h-6 text-[#3A2D27] mb-3" />
            <div className="label-eyebrow mb-3">Dispozice výrobků</div>
            {prodAvail.length === 0 ? (
              <div className="text-sm text-[#57534E]">Zatím nic na výrobním skladě.</div>
            ) : (
              <ul className="space-y-2">
                {prodAvail.slice(0, 5).map((a) => {
                  const product = products.find((p) => p.id === a.product_id);
                  return (
                    <li key={`${a.product_id}-${a.warehouse_id}`} className="flex justify-between text-sm">
                      <span>{productName(a.product_id)}</span>
                      <span className="text-right">
                        <span className="text-[#376A4D] font-medium">
                          {formatQtyWithPackage(a.available ?? a.quantity, product, "ks")}
                        </span>
                        {(a.pending_dispatch || 0) > 0 && (
                          <span className="text-[#9E4747] block text-xs">
                            v exp. {formatQtyWithPackage(a.pending_dispatch, product, "ks")}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {user?.role === "admin" && wo && (
          <div className="card-cafe p-6">
            <div className="flex items-center gap-2 mb-4">
              <Trash2 className="w-5 h-5 text-[#9E4747]" />
              <div>
                <div className="label-eyebrow mb-0.5">Odpisy</div>
                <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Přehled za pobočky
                </h2>
              </div>
              <div className="ml-auto text-right text-sm text-[#57534E]">
                <div>
                  <span className="label-eyebrow mr-2">Období</span>
                  <span className="mono">{formatCZK(wo.today?.cost || 0)}</span>{" "}
                  <span className="text-xs">/ {formatQty(wo.today?.qty || 0)} ks</span>
                </div>
                <div>
                  <span className="label-eyebrow mr-2">Celkem</span>
                  <span className="mono">{formatCZK(wo.total?.cost || 0)}</span>{" "}
                  <span className="text-xs">/ {formatQty(wo.total?.qty || 0)} ks</span>
                </div>
              </div>
            </div>
            {wo.per_branch.length === 0 ? (
              <div className="text-sm text-[#57534E] py-6 text-center border border-dashed border-[#E5E1D8] rounded-md">
                Zatím žádné odpisy.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[#E5E1D8]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F6F0]">
                    <tr className="text-left">
                      <th className="p-3 font-medium text-[#57534E]">Pobočka</th>
                      <th className="p-3 font-medium text-[#57534E] text-right">Počet záznamů</th>
                      <th className="p-3 font-medium text-[#57534E] text-right">Odepsáno ks</th>
                      <th className="p-3 font-medium text-[#57534E] text-right">Náklady</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wo.per_branch
                      .slice()
                      .sort((a, b) => (b.cost || 0) - (a.cost || 0))
                      .map((r) => (
                        <tr key={r.branch_id} className="border-t border-[#E5E1D8]">
                          <td className="p-3 font-medium">{branchName(r.branch_id)}</td>
                          <td className="p-3 text-right">{r.count}</td>
                          <td className="p-3 text-right">{formatQty(r.qty, "ks")}</td>
                          <td className="p-3 text-right font-medium text-[#9E4747]">
                            {formatCZK(r.cost)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="card-cafe p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow mb-1">Poslední výroby</div>
              <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Historie výroby
              </h2>
            </div>
            <Link
              to="/historie"
              className="text-sm text-[#3A2D27] hover:text-[#D4A373] transition-colors inline-flex items-center gap-1"
              data-testid="dashboard-history-link"
            >
              <HistoryIcon className="w-4 h-4" /> Detail
            </Link>
          </div>

          {recentRuns.length === 0 ? (
            <div className="text-sm text-[#57534E] py-8 text-center">
              Zatím nebyla zaznamenána žádná výroba.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-[#E5E1D8]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-3 font-medium text-[#57534E]">Šarže</th>
                    <th className="p-3 font-medium text-[#57534E]">Výrobek</th>
                    <th className="p-3 font-medium text-[#57534E] text-right">Ks</th>
                    <th className="p-3 font-medium text-[#57534E] text-right">Nákl./ks</th>
                    <th className="p-3 font-medium text-[#57534E] text-right">vs Plán</th>
                    <th className="p-3 font-medium text-[#57534E] text-right">Celkem</th>
                    <th className="p-3 font-medium text-[#57534E]">Čas</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map((r) => {
                    const product = products.find((p) => p.id === r.product_id);
                    return (
                      <tr key={r.id} className="border-t border-[#E5E1D8]">
                        <td className="p-3 mono text-xs">{r.batch_code}</td>
                        <td className="p-3">{productName(r.product_id)}</td>
                        <td className="p-3 text-right">{formatQtyWithPackage(r.quantity, product)}</td>
                        <td className="p-3 text-right">{formatCZK(r.unit_cost)}</td>
                        <td className="p-3 text-right">
                          <CostVarianceBadge actualUnitCost={r.unit_cost} product={product} />
                        </td>
                        <td className="p-3 text-right font-medium">{formatCZK(r.total_cost)}</td>
                        <td className="p-3 text-[#57534E]">{formatDateTime(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
